import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from '@/integrations/supabase/client';

interface MapLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
  title?: string;
  description?: string;
  type: 'customer' | 'tradesman' | 'current';
}

interface TrackingMapProps {
  locations: MapLocation[];
  className?: string;
  centerLat?: number;
  centerLng?: number;
  zoom?: number;
  showNavigation?: boolean;
  interactive?: boolean;
  fitToLocations?: boolean;
}

export const TrackingMap: React.FC<TrackingMapProps> = ({
  locations,
  className = '',
  centerLat = 51.505,
  centerLng = -0.09,
  zoom = 13,
  showNavigation = true,
  interactive = false,
  fitToLocations = false,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(!interactive);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  // Function to center map without animation
  const centerMap = (longitude: number, latitude: number) => {
    if (!map.current) return;
    
    // Use setCenter instead of flyTo to avoid animation
    map.current.setCenter([longitude, latitude]);
  };

  useEffect(() => {
    console.log('=== TrackingMap: Component mounted ===');
    
    const fetchToken = async () => {
      try {
        console.log('TrackingMap: About to call get-mapbox-token function...');
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        
        console.log('TrackingMap: Raw response:', JSON.stringify({ data, error }, null, 2));
        console.log('TrackingMap: Response status check - data exists:', !!data, 'error exists:', !!error);
        
        if (error) {
          console.error('TrackingMap: Function returned error:', error);
          console.error('TrackingMap: Error details:', {
            message: error.message,
            status: error.status,
            statusText: error.statusText,
            details: error.details
          });
          setError(`API Error: ${error.message || error.details || JSON.stringify(error)}`);
          return;
        }
        
        if (!data) {
          console.error('TrackingMap: No data in response');
          setError('No response from map configuration service');
          return;
        }
        
        if (!data.token) {
          console.error('TrackingMap: No token in data:', data);
          setError('Map API token missing from configuration');
          return;
        }
        
        console.log('TrackingMap: SUCCESS - Got token with length:', data.token.length);
        setMapboxToken(data.token);
      } catch (err) {
        console.error('TrackingMap: Exception caught:', err);
        setError(`Network Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    
    fetchToken();
  }, []);

  useEffect(() => {
    if (!mapContainer.current || !mapboxToken) {
      console.log('TrackingMap: Map initialization skipped - container or token missing', {
        hasContainer: !!mapContainer.current,
        hasToken: !!mapboxToken,
        containerDimensions: mapContainer.current ? {
          width: mapContainer.current.offsetWidth,
          height: mapContainer.current.offsetHeight
        } : null
      });
      return;
    }

    // Don't reinitialize if map already exists
    if (map.current) {
      console.log('TrackingMap: Map already initialized, skipping...');
      return;
    }

    console.log('TrackingMap: Initializing Mapbox...');
    console.log('TrackingMap: Container dimensions:', {
      width: mapContainer.current.offsetWidth,
      height: mapContainer.current.offsetHeight
    });
    
    try {
      // Set Mapbox access token
      mapboxgl.accessToken = mapboxToken;
      
      // Initialize Mapbox map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [centerLng, centerLat],
        zoom: zoom,
      });

      // Configure map interactions based on interactive prop
      if (!interactive || isLocked) {
        map.current.scrollZoom.disable();
        map.current.doubleClickZoom.disable();
        map.current.boxZoom.disable();
        map.current.keyboard.disable();
        map.current.dragPan.disable();
        map.current.touchZoomRotate.disable();
        map.current.setMinZoom(zoom);
        map.current.setMaxZoom(zoom);
      } else {
        map.current.scrollZoom.enable();
        map.current.doubleClickZoom.enable();
        map.current.boxZoom.enable();
        map.current.keyboard.enable();
        map.current.dragPan.enable();
        map.current.touchZoomRotate.enable();
      }

      // Listen for map load and errors for better diagnostics
      map.current.on('load', () => {
        console.log('TrackingMap: Mapbox style loaded successfully');
      });

      map.current.on('error', (evt: any) => {
        console.error('TrackingMap: Mapbox emitted error event:', evt);
        const status = evt?.error?.status || evt?.error?.statusCode;
        if (status === 401 || status === 403) {
          setError('Map authorization failed. Please set a valid Mapbox public token (starts with "pk.") in Supabase secrets.');
        } else {
          setError('Map resources failed to load. Please check your network and token configuration.');
        }
      });

      // Add navigation controls if requested
      if (showNavigation && interactive) {
        map.current.addControl(
          new mapboxgl.NavigationControl({
            visualizePitch: true,
          }),
          'top-right'
        );
        
        // Add fullscreen control - target the parent container for proper fullscreen
        map.current.addControl(
          new mapboxgl.FullscreenControl({
            container: mapContainer.current.parentElement || undefined
          }),
          'top-right'
        );
      }

      console.log('TrackingMap: Mapbox object created successfully');
    } catch (mapError) {
      console.error('TrackingMap: Error creating Mapbox:', mapError);
      setError('Failed to initialize map. Please refresh the page.');
    }

    // Cleanup function
    return () => {
      console.log('TrackingMap: Cleaning up map');
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapboxToken, interactive, isLocked]); // Dependencies for map configuration

  useEffect(() => {
    if (!map.current || !mapboxToken) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers without changing map position
    locations.forEach(location => {
      const markerColor = location.type === 'customer' ? '#3b82f6' : 
                         location.type === 'tradesman' ? '#f97316' : '#10b981';
      
      // Create marker element
      const markerElement = document.createElement('div');
      markerElement.style.backgroundColor = markerColor;
      markerElement.style.width = location.type === 'current' ? '24px' : '20px';
      markerElement.style.height = location.type === 'current' ? '24px' : '20px';
      markerElement.style.borderRadius = '50%';
      markerElement.style.border = '2px solid #ffffff';
      markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      
      const marker = new mapboxgl.Marker(markerElement)
        .setLngLat([location.longitude, location.latitude])
        .addTo(map.current!);

      // Add popup if title is provided
      if (location.title) {
        const popupContent = `
          <div style="padding: 8px;">
            <h3 style="font-weight: 600; margin: 0 0 4px 0; font-size: 14px;">${location.title}</h3>
            ${location.description ? `<p style="font-size: 12px; color: #666; margin: 0;">${location.description}</p>` : ''}
          </div>
        `;
        
        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: true,
          closeOnClick: false
        }).setHTML(popupContent);
        
        marker.setPopup(popup);
      }

      markersRef.current.push(marker);
    });

    // Fit map to locations if requested and locations exist
    if (fitToLocations && locations.length > 0 && map.current) {
      const bounds = new mapboxgl.LngLatBounds();
      locations.forEach(location => {
        bounds.extend([location.longitude, location.latitude]);
      });
      
      // Add padding and fit to bounds
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 16,
        duration: 1000
      });
    }
  }, [locations, mapboxToken, fitToLocations]);

  if (!mapboxToken) {
    console.log('TrackingMap: Rendering loading state, token status:', !!mapboxToken, 'error:', error);
    return (
      <div className={`flex items-center justify-center bg-muted rounded-lg ${className}`} style={{ minHeight: '300px' }}>
        <div className="text-center p-8">
          {error ? (
            <>
              <div className="text-destructive text-lg mb-4">⚠️</div>
              <p className="text-destructive font-medium mb-2">Map Configuration Error</p>
              <p className="text-destructive/80 text-sm">{error}</p>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading Mapbox...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const toggleMapLock = () => {
    if (!map.current || !interactive) return;
    
    const newLockState = !isLocked;
    setIsLocked(newLockState);
    
    if (newLockState) {
      // Lock the map
      map.current.scrollZoom.disable();
      map.current.doubleClickZoom.disable();
      map.current.boxZoom.disable();
      map.current.keyboard.disable();
      map.current.dragPan.disable();
      map.current.touchZoomRotate.disable();
    } else {
      // Unlock the map
      map.current.scrollZoom.enable();
      map.current.doubleClickZoom.enable();
      map.current.boxZoom.enable();
      map.current.keyboard.enable();
      map.current.dragPan.enable();
      map.current.touchZoomRotate.enable();
    }
  };

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ minHeight: '300px' }}>
      <div 
        ref={mapContainer} 
        className="absolute inset-0 rounded-lg"
        style={{ 
          width: '100%', 
          height: '100%',
          minHeight: '300px'
        }} 
      />
      
      {/* Interactive controls */}
      {interactive && (
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={toggleMapLock}
            className="bg-white/90 hover:bg-white text-gray-700 px-3 py-2 rounded-lg shadow-md text-sm font-medium border border-gray-200 transition-colors"
          >
            {isLocked ? '🔒 Locked' : '🔓 Interactive'}
          </button>
        </div>
      )}
      
      {locations.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/80 rounded-lg">
          <p className="text-muted-foreground">No location data available</p>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-destructive/10 rounded-lg border border-destructive">
          <div className="text-center p-4">
            <p className="text-destructive font-medium mb-2">Map Error</p>
            <p className="text-destructive/80 text-sm">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};